import { useEffect } from 'react'

import { useNamedQuery } from '~/api/reports'
import { SadStates } from '~/components/SadStates'
import { jobsFilterAdapter } from '~/dashboards/filters/adapters/jobs'
import spendFilterAdapters from '~/dashboards/filters/adapters/spend'
import workOrderFilterAdapters from '~/dashboards/filters/adapters/work_orders'
import {
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatCardContainer,
  StatLabel,
  StatNumber,
  useMediaQuery
} from '~/design_system'
import { featureFlags, useFeatureFlag } from '~/hooks/useFeatureFlag'
import { useChartFormat } from '~/design_system/components/charts/format'
import { woStatus } from '~/work_order_list/types_enums'
import { useMsg } from '~/common/localizationUtils'
import {
  DashboardPageContainer,
  DashboardPageHeader,
  DEFAULT_INVOICE_STATUS,
  DEFAULT_JOB_STATUS,
  useWidgetReportUrl
} from './common'
import { getFilterParams } from './filters/utils'
import { updateDashboardQueryParams, useDashboardReport, useRowOnlyData } from './hooks'
import { JobsByCategory, PositionsComparison } from './Jobs'
import { getLast6MonthsIfNoBillingCycleFilters, SpendByLineType, SpendByMonth } from './Spend'
import { WorkOrdersByCountry, WorkOrdersBySource, WorkOrdersByState } from './WorkOrders'

export const defaultSpendFilters = [{ id: 'invoice_status', operator: 'any_of', value: DEFAULT_INVOICE_STATUS }]
const defaultJobFilters = [{ id: 'job_status', operator: 'equal', value: DEFAULT_JOB_STATUS }]
const defaultWorkOrderFilters = [{ id: 'status', operator: 'equal', value: woStatus.ACTIVE }]

const useUpdateFiltersEffect = () => {
  const last6MonthsFilters = getLast6MonthsIfNoBillingCycleFilters(defaultSpendFilters)
  const spendByMonthFilters = getFilterParams(last6MonthsFilters, spendFilterAdapters)

  const bulkDashboardQueryParams = {
    work_orders_by_source: { filters: getFilterParams(defaultWorkOrderFilters, workOrderFilterAdapters) },
    work_orders_by_country: { filters: getFilterParams(defaultWorkOrderFilters, workOrderFilterAdapters) },
    work_orders_by_state: { filters: getFilterParams(defaultWorkOrderFilters, workOrderFilterAdapters) },
    jobs_by_category: { filters: getFilterParams(defaultJobFilters, jobsFilterAdapter) },
    positions_comparison: { filters: getFilterParams(defaultJobFilters, jobsFilterAdapter) },
    spend_by_line_type: { filters: getFilterParams(defaultSpendFilters, spendFilterAdapters) },
    spend_by_month: { filters: spendByMonthFilters }
  }

  useEffect(() => {
    updateDashboardQueryParams(bulkDashboardQueryParams)
  }, [])
}

export const Summary = () => {
  const msg = useMsg()
  const screenWidthToProjectionScale = [400, 650, 500, 600, 650]
  const activeMediaQueries = useMediaQuery([
    '(min-width: 0px)',
    '(min-width: 600px)',
    '(min-width: 900px)',
    '(min-width: 1200px)',
    '(min-width: 1500px)'
  ])
  const activeMediaQueryIndex = activeMediaQueries.findLastIndex(Boolean)
  const projectionScale = screenWidthToProjectionScale[activeMediaQueryIndex] ?? 500
  const { data: showUSMaps } = useFeatureFlag(featureFlags.SHOW_US_MAPS)
  const { data: showWorldMaps } = useFeatureFlag(featureFlags.SHOW_WORLD_MAPS)

  useUpdateFiltersEffect()

  return (
    <>
      <DashboardPageHeader title={msg('common.ui.summary')} />
      <DashboardPageContainer enableFilters={false}>
        <Stack spacing={4}>
          <SimpleGrid columns={{ base: 2, sm: 4, lg: 6 }} gap={4}>
            <HeadCountStat />
            <JobsCountStat />
            <WorkOrderCountStat />
            <VendorsCountStat />
            <ActiveUsersCountStat />
            <TotalSpendCountStat />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <WorkOrdersBySource filters={defaultWorkOrderFilters} />
            {showWorldMaps && <WorkOrdersByCountry filters={defaultWorkOrderFilters} />}
            {showUSMaps && <WorkOrdersByState projectionScale={projectionScale} filters={defaultWorkOrderFilters} />}
            <JobsByCategory filters={defaultJobFilters} />
            <PositionsComparison filters={defaultJobFilters} />
            <SpendByLineType filters={defaultSpendFilters} />
            <SpendByMonth filters={defaultSpendFilters} />
          </SimpleGrid>
        </Stack>
      </DashboardPageContainer>
    </>
  )
}

const HeadCountStat = () => {
  const msg = useMsg()
  const reportKey = 'total_headcount'
  const { data, isLoading, isError, errorCode } = useDashboardReport({
    id: 'total_headcount',
    queryName: reportKey,
    useTransform: false
  })
  const value = data === undefined && errorCode === 403 ? 0 : data?.[0]?.['Total Headcount']
  const { url: widgetReportUrl } = useWidgetReportUrl()

  return (
    <StatCardContainer
      customReportUrl={`${widgetReportUrl}?&key=${reportKey}`}
      isError={data === undefined && errorCode === 403 ? false : isError}>
      <SummaryStatCard id="headcount" isLoading={isLoading} value={value} label={msg('term.headcount')} />
    </StatCardContainer>
  )
}

const JobsCountStat = () => {
  const msg = useMsg()
  const { data, isLoading, isError } = useRowOnlyData({
    id: 'job_analysis',
    field: 'job_count',
    filters: defaultJobFilters
  })
  const { url: widgetReportUrl } = useWidgetReportUrl()
  const activeJobListTemplateUrl = `${widgetReportUrl}?&key=active_job_list`

  return (
    <StatCardContainer isError={isError} customReportUrl={activeJobListTemplateUrl}>
      <SummaryStatCard
        id="jobs"
        label={msg('term.job', { count: 999 })}
        isLoading={isLoading}
        value={data?.[0]?.value ?? 0}
      />
    </StatCardContainer>
  )
}

const WorkOrderCountStat = () => {
  const msg = useMsg()
  const reportKey = 'work_order_count'
  const { data, isLoading, isError, errorCode } = useDashboardReport({
    id: 'work_order_count',
    queryName: reportKey,
    useTransform: false
  })
  const value = data === undefined && errorCode === 403 ? 0 : data?.[0]?.['Work Order Count']
  const { url: widgetReportUrl } = useWidgetReportUrl()

  return (
    <StatCardContainer
      customReportUrl={`${widgetReportUrl}?&key=${reportKey}`}
      isError={data === undefined && errorCode === 403 ? false : isError}>
      <SummaryStatCard
        id="work_orders"
        isLoading={isLoading}
        value={value}
        label={msg('term.workorder', { count: 999 })}
      />
    </StatCardContainer>
  )
}

const VendorsCountStat = () => {
  const msg = useMsg()
  const { data, isLoading, isError } = useRowOnlyData({
    id: 'vendor_entity_analysis',
    field: 'vendor_entity_count',
    filters: [
      {
        id: 've_was_onboarded',
        field: 've_was_onboarded',
        path: 've',
        operator: 'equal',
        value: [true]
      },
      {
        id: 've_status',
        field: 've_status',
        path: 've',
        operator: 'not_equal',
        value: [5, 6]
      }
    ]
  })
  const { url: widgetReportUrl } = useWidgetReportUrl()

  return (
    <StatCardContainer isError={isError} customReportUrl={`${widgetReportUrl}?&key=vendor_companies`}>
      <SummaryStatCard
        id="vendors"
        label={msg('term.vendor', { count: 999 })}
        isLoading={isLoading}
        value={data?.[0]?.value ?? 0}
      />
    </StatCardContainer>
  )
}

const ActiveUsersCountStat = () => {
  const msg = useMsg()
  const reportKey = 'active_users_count'
  const { data, isLoading, isError, errorCode } = useDashboardReport({
    id: 'total_active_users',
    queryName: reportKey,
    useTransform: false
  })
  const value = data === undefined && errorCode === 403 ? 0 : data?.[0]?.['Total Active Users']
  const { url: widgetReportUrl } = useWidgetReportUrl()

  return (
    <StatCardContainer
      customReportUrl={`${widgetReportUrl}?&key=${reportKey}`}
      isError={data === undefined && errorCode === 403 ? false : isError}>
      <SummaryStatCard id="active_users" isLoading={isLoading} value={value} label={msg('term.active_users')} />
    </StatCardContainer>
  )
}

const TotalSpendCountStat = () => {
  const msg = useMsg()
  const {
    data: { data } = {},
    isLoading,
    isError,
    status,
    error
  } = useNamedQuery({
    queryName: 'spend_analysis',
    namedQueryParams: { fields: ['total_amount', 'base_currency_code'], filters: defaultSpendFilters },
    page: 0,
    pageSize: 1,
    urlParams: {}
  })
  // @ts-ignore
  const errorCode = status === 'error' ? error?.status : null

  const spendFormat = useChartFormat('currency', false)

  const [amount = null, currencyCode = null] = data?.[0] ?? []
  const defaultValue = currencyCode && spendFormat(amount)
  const value = data === undefined && errorCode === 403 ? 0 : defaultValue
  const { url: widgetReportUrl } = useWidgetReportUrl()

  return (
    <StatCardContainer
      isError={data === undefined && errorCode === 403 ? false : isError}
      customReportUrl={`${widgetReportUrl}?&key=invoice_reconciliation_report`}>
      <SummaryStatCard id="total_spend" label={msg('term.total_spend')} isLoading={isLoading} value={value} />
    </StatCardContainer>
  )
}

type SummaryStatCardProps = {
  id: string
  isLoading: boolean
  label: string
  value: number
}

const SummaryStatCard = ({ id, isLoading, label, value }: SummaryStatCardProps) => (
  <SadStates states={[{ when: isLoading, render: () => <StatChartSkeleton /> }]}>
    <Stat data-testid={`${id}_card`}>
      <StatLabel>{label}</StatLabel>
      <StatNumber fontWeight="normal">{value ?? 0}</StatNumber>
    </Stat>
  </SadStates>
)

const StatChartSkeleton = () => (
  <Stack spacing={2}>
    <Skeleton height="16px" width="60px" />
    <Skeleton height="32px" width="30px" />
  </Stack>
)
